import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { provisionApi, ApiError } from "@/api";
import type {
  ProvisionPlan,
  ProvisionProposedJob,
  ProvisionProposedClient,
  ProvisionJobCandidate,
  ProvisionClientCandidate,
  ProvisionRequest,
  ProvisionJobAction,
  ProvisionClientAction,
  ProvisionFeeAction,
  ProvisionDrawingSetAction,
} from "@/types";
import { Button, Spinner, Pill, TextField, Toast, useToast } from "@/components/ui";
import { formatMoney } from "@/lib/money";
import { resolveTokens } from "@/lib/lineItems";
import { useAcceptedCount } from "./AcceptedCountContext";
import "./provision.css";

/* ─── Working state ─────────────────────────────────────────────────────────
 * The plan from the server is immutable; the user edits this working copy and
 * commits it once. Everything below mirrors a card's controls. */

type JobMode = "create" | "link";
type ClientMode = "create" | "link" | "inherit" | "skip";
type DrawingSetMode = "create" | "close_and_new" | "keep" | "skip";

interface SeedRow { drawing_type: string; title: string }

interface FeeRow {
  source_quote_line_id: number;
  include: boolean;
  description: string;
  amount: number;
  fee_type: string;
}

interface WorkingState {
  jobMode: JobMode;
  proposedJob: ProvisionProposedJob;
  linkedJobId: number | null;
  clientMode: ClientMode;
  proposedClient: ProvisionProposedClient;
  linkedClientId: number | null;
  fees: FeeRow[];
  dsMode: DrawingSetMode;
  dsLabel: string;
  dsSeeds: SeedRow[];
}

/** Build the initial working copy from the server plan. Fees join the accepted
 *  lines with the fee suggestions on source_quote_line_id; when a line has no
 *  suggestion we fall back to the line's own title/total/fee_type. */
function initWorking(plan: ProvisionPlan): WorkingState {
  const feeBySource = new Map(plan.suggestions.fees.map((f) => [f.source_quote_line_id, f]));
  // Line-item text can carry the {{short_description}} token (quotes made from
  // the CD template). The quote renderer resolves it at render time; here the
  // text becomes a persisted fee description, so resolve it before seeding the
  // editable rows — otherwise the literal token gets committed to the job.
  const shortDescription = plan.quote.project_description;
  const fees: FeeRow[] = plan.accepted_lines.map((line) => {
    const sug = feeBySource.get(line.id);
    return {
      source_quote_line_id: line.id,
      include: true,
      description: resolveTokens(sug?.proposed.description ?? line.title, shortDescription),
      amount: sug?.proposed.amount ?? line.line_total,
      fee_type: sug?.proposed.fee_type ?? line.fee_type,
    };
  });

  const firstCandidate = plan.suggestions.job.candidates[0] ?? null;

  // The set's label is the quote's short description (project_description, the
  // field behind the {{short_description}} token). Seeded drawings inherit it so
  // they match the set title — and stay consistent with manually-added drawings,
  // which inherit the set label too. Fall back to the line's own title when the
  // quote has no short description.
  const dsLabel = plan.suggestions.drawing_set.proposed.label;

  return {
    jobMode: plan.suggestions.job.default_action,
    proposedJob: { ...plan.suggestions.job.proposed_new },
    linkedJobId: plan.suggestions.job.default_action === "link" ? firstCandidate?.id ?? null : null,
    clientMode: plan.suggestions.client.default_action,
    proposedClient: { ...plan.suggestions.client.proposed_new },
    linkedClientId:
      plan.suggestions.client.default_action === "link"
        ? plan.suggestions.client.candidates[0]?.id ?? null
        : null,
    fees,
    dsMode: "create",
    dsLabel,
    dsSeeds: plan.suggestions.drawing_set.seed_drawings.map((s) => ({
      drawing_type: s.drawing_type,
      title: dsLabel.trim() || resolveTokens(s.title, shortDescription),
    })),
  };
}

export function ProvisionWorkbench() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const id = Number(quoteId);
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const { refresh: refreshAcceptedCount } = useAcceptedCount();

  const [plan, setPlan] = useState<ProvisionPlan | null>(null);
  const [w, setW] = useState<WorkingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [committing, setCommitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setError("Invalid quote id.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    provisionApi
      .getProvisionPlan(id)
      .then((res) => {
        if (cancelled) return;
        setPlan(res);
        setW(initWorking(res));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the provision plan.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // The chosen linked candidate (when in Link mode) drives the client + drawing
  // set cards: a linked job inherits its client and may already own an open set.
  const linkedCandidate = useMemo<ProvisionJobCandidate | null>(() => {
    if (!plan || !w || w.jobMode !== "link" || w.linkedJobId == null) return null;
    return plan.suggestions.job.candidates.find((c) => c.id === w.linkedJobId) ?? null;
  }, [plan, w]);

  const linkedHasOpenSet = !!linkedCandidate?.current_drawing_set;

  // Keep the drawing-set mode coherent with the chosen job. A linked job that
  // already owns an open set defaults to "keep" (no-op); anything else (new job,
  // or a linked job without a set) defaults to "create" a fresh seeded set. The
  // user can still override to skip/close_and_new within the card. We only nudge
  // when the current mode is invalid for the new context (never clobber a manual
  // skip/close choice that's still valid).
  useEffect(() => {
    setW((prev) => {
      if (!prev) return prev;
      if (linkedHasOpenSet) {
        // Open-set context allows keep / close_and_new / skip — "create" is not
        // valid here, so fall back to the "keep" default.
        if (prev.dsMode === "create") return { ...prev, dsMode: "keep" };
      } else {
        // No-open-set context allows create / skip — "keep"/"close_and_new" are
        // not valid, so fall back to the "create" default.
        if (prev.dsMode === "keep" || prev.dsMode === "close_and_new") return { ...prev, dsMode: "create" };
      }
      return prev;
    });
  }, [linkedHasOpenSet]);

  // Total of the fees the user has ticked, for the warn-if-differs footer.
  const includedFeeTotal = useMemo(
    () => (w ? w.fees.filter((f) => f.include).reduce((sum, f) => sum + (Number(f.amount) || 0), 0) : 0),
    [w],
  );

  function patch(next: Partial<WorkingState>) {
    setW((prev) => (prev ? { ...prev, ...next } : prev));
  }

  // When job flips to Link, the client is inherited (forced, card disabled). When
  // it flips back to Create, restore the plan's default client action.
  function setJobMode(mode: JobMode) {
    if (!plan || !w) return;
    if (mode === "link") {
      patch({
        jobMode: "link",
        linkedJobId: w.linkedJobId ?? plan.suggestions.job.candidates[0]?.id ?? null,
        clientMode: "inherit",
      });
    } else {
      patch({ jobMode: "create", clientMode: plan.suggestions.client.default_action });
    }
  }

  /** The brief asks us to block a commit where every card resolves to skip. The
   *  job card is always Create or Link (never skip), so a real "everything
   *  skipped" can't occur — the job is always materialised. The guard stays for
   *  documentation; today it never blocks the commit. */
  const allSkipped = false;

  function buildRequest(): ProvisionRequest {
    if (!w) throw new Error("no working state");

    const job: ProvisionJobAction =
      w.jobMode === "create"
        ? { action: "create", data: w.proposedJob }
        : { action: "link", job_id: w.linkedJobId ?? 0 };

    let client: ProvisionClientAction;
    if (w.jobMode === "link") {
      client = { action: "inherit" };
    } else if (w.clientMode === "create") {
      client = { action: "create", data: w.proposedClient };
    } else if (w.clientMode === "link") {
      client = { action: "link", client_id: w.linkedClientId ?? 0 };
    } else if (w.clientMode === "inherit") {
      client = { action: "inherit" };
    } else {
      client = { action: "skip" };
    }

    const fees: ProvisionFeeAction[] = w.fees.map((f) =>
      f.include
        ? {
            action: "create",
            source_quote_line_id: f.source_quote_line_id,
            data: { description: f.description, amount: Number(f.amount) || 0, fee_type: f.fee_type },
          }
        : { action: "skip", source_quote_line_id: f.source_quote_line_id },
    );

    let drawing_set: ProvisionDrawingSetAction;
    if (w.dsMode === "create" || w.dsMode === "close_and_new") {
      drawing_set = {
        action: w.dsMode,
        data: { label: w.dsLabel },
        seed: w.dsSeeds.map((s) => ({ drawing_type: s.drawing_type, title: s.title })),
      };
    } else if (w.dsMode === "keep") {
      drawing_set = { action: "keep" };
    } else {
      drawing_set = { action: "skip" };
    }

    return { job, client, fees, drawing_set };
  }

  async function commit() {
    if (!w || committing) return;
    setConfirming(false);
    setCommitting(true);
    setError(undefined);
    try {
      const res = await provisionApi.provisionQuote(id, buildRequest());
      // The quote just left the accepted queue — drop the nav badges now
      // rather than waiting for the next poll.
      void refreshAcceptedCount();
      const linked = w.jobMode === "link";
      navigate(`/staff/jobs/${res.created.job_id}`, {
        state: {
          flash: {
            kind: "info" as const,
            message: linked ? "Provisioned — linked to job." : "Provisioned — job created.",
          },
        },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not provision this quote.");
      showToast(err instanceof ApiError ? err.message : "Could not provision this quote.", "warn");
      setCommitting(false);
    }
  }

  if (loading) {
    return (
      <section className="provision">
        <div className="provision__loading"><Spinner label="Loading provision plan…" /></div>
      </section>
    );
  }

  if (error && !plan) {
    return (
      <section className="provision">
        <Link to="/staff/quotes/accepted" className="provision__back">← Accepted quotes</Link>
        <p className="leads__error" role="alert">{error}</p>
      </section>
    );
  }

  if (!plan || !w) return null;

  const includeFeeCount = w.fees.filter((f) => f.include).length;

  // Live footer summary — what the commit will materialise.
  const summary: string[] = [];
  summary.push(w.jobMode === "create" ? "Create job" : "Link job");
  if (w.jobMode === "link") summary.push("Inherit client");
  else if (w.clientMode === "create") summary.push("Create client");
  else if (w.clientMode === "link") summary.push("Link client");
  else if (w.clientMode === "skip") summary.push("Skip client");
  if (includeFeeCount > 0) summary.push(`${includeFeeCount} fee${includeFeeCount === 1 ? "" : "s"}`);
  if (w.dsMode === "create") summary.push("New drawing set");
  else if (w.dsMode === "close_and_new") summary.push("Close & re-open drawing set");
  else if (w.dsMode === "keep") summary.push("Keep current set");

  return (
    <section className="provision">
      <Link to="/staff/quotes/accepted" className="provision__back">← Accepted quotes</Link>

      <header className="provision__head">
        <div>
          <h1>Provision {plan.quote.quote_number}</h1>
          <p className="provision__sub">
            {plan.accepted_lines.length} accepted line{plan.accepted_lines.length === 1 ? "" : "s"} ·{" "}
            {formatMoney(plan.quote.accepted_total)} accepted
          </p>
        </div>
        <Pill tone="success">{plan.quote.status}</Pill>
      </header>

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <div className="provision__cards">
        <JobCard
          plan={plan}
          mode={w.jobMode}
          proposed={w.proposedJob}
          linkedJobId={w.linkedJobId}
          onMode={setJobMode}
          onPickCandidate={(jobId) => patch({ linkedJobId: jobId })}
          onProposed={(p) => patch({ proposedJob: { ...w.proposedJob, ...p } })}
        />

        <ClientCard
          plan={plan}
          jobLinked={w.jobMode === "link"}
          mode={w.clientMode}
          proposed={w.proposedClient}
          linkedClientId={w.linkedClientId}
          onMode={(m) => patch({ clientMode: m })}
          onPickCandidate={(clientId) => patch({ linkedClientId: clientId })}
          onProposed={(p) => patch({ proposedClient: { ...w.proposedClient, ...p } })}
        />

        <FeesCard
          fees={w.fees}
          acceptedTotal={plan.quote.accepted_total}
          includedTotal={includedFeeTotal}
          onChange={(rows) => patch({ fees: rows })}
        />

        <DrawingSetCard
          linkedCandidate={linkedCandidate}
          linkedHasOpenSet={linkedHasOpenSet}
          mode={w.dsMode}
          label={w.dsLabel}
          seeds={w.dsSeeds}
          onMode={(m) => patch({ dsMode: m })}
          onLabel={(label) => patch({ dsLabel: label })}
          onSeeds={(seeds) => patch({ dsSeeds: seeds })}
        />
      </div>

      <footer className="provision__footer">
        <ul className="provision__summary">
          {summary.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <Button
          variant="primary"
          loading={committing}
          disabled={committing || allSkipped}
          onClick={() => setConfirming(true)}
        >
          Commit
        </Button>
      </footer>

      {confirming ? (
        <div className="provision__confirm-overlay" role="dialog" aria-modal="true">
          <div className="provision__confirm">
            <h2>Commit provisioning?</h2>
            <p>This will: {summary.join(", ")}.</p>
            <p className="provision__confirm-note">This can't be undone from here.</p>
            <div className="provision__confirm-actions">
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={committing}>Cancel</Button>
              <Button variant="primary" onClick={commit} loading={committing}>Commit</Button>
            </div>
          </div>
        </div>
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

/* ─── Job card ─────────────────────────────────────────────────────────────── */

function JobCard({
  plan,
  mode,
  proposed,
  linkedJobId,
  onMode,
  onPickCandidate,
  onProposed,
}: {
  plan: ProvisionPlan;
  mode: JobMode;
  proposed: ProvisionProposedJob;
  linkedJobId: number | null;
  onMode: (m: JobMode) => void;
  onPickCandidate: (jobId: number) => void;
  onProposed: (p: Partial<ProvisionProposedJob>) => void;
}) {
  const candidates = plan.suggestions.job.candidates;
  return (
    <article className="prov-card">
      <header className="prov-card__head">
        <h2>Job</h2>
        <ModeRadios
          name="job-mode"
          value={mode}
          options={[
            { value: "create", label: "Create new" },
            { value: "link", label: "Link existing", disabled: candidates.length === 0 },
          ]}
          onChange={(v) => onMode(v as JobMode)}
        />
      </header>

      {mode === "link" ? (
        candidates.length === 0 ? (
          <p className="prov-card__muted">No matching jobs to link.</p>
        ) : (
          <ul className="prov-candidates">
            {candidates.map((c) => (
              <li key={c.id}>
                <label className={`prov-candidate${linkedJobId === c.id ? " is-on" : ""}`}>
                  <input
                    type="radio"
                    name="job-candidate"
                    checked={linkedJobId === c.id}
                    onChange={() => onPickCandidate(c.id)}
                  />
                  <div className="prov-candidate__body">
                    <div className="prov-candidate__top">
                      <strong>{c.job_name || "Untitled job"}</strong>
                      <Pill tone="info-soft">Score {c.score}</Pill>
                    </div>
                    <span className="prov-candidate__sub">
                      {[c.full_address, c.postcode].filter(Boolean).join(" · ") || "—"}
                    </span>
                    {c.match_reason ? <span className="prov-candidate__reason">{c.match_reason}</span> : null}
                    {c.current_drawing_set ? (
                      <span className="prov-candidate__set">
                        Open set #{c.current_drawing_set.set_number} ·{" "}
                        {c.current_drawing_set.header_status} · {c.current_drawing_set.drawing_count} drawing
                        {c.current_drawing_set.drawing_count === 1 ? "" : "s"}
                        {c.current_drawing_set.is_complete ? " · complete" : ""}
                      </span>
                    ) : (
                      <span className="prov-candidate__set prov-card__muted">No open drawing set</span>
                    )}
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="prov-form">
          <TextField label="Job name" value={proposed.job_name} onChange={(e) => onProposed({ job_name: e.target.value })} />
          <TextField label="Full address" value={proposed.full_address} onChange={(e) => onProposed({ full_address: e.target.value })} />
          <div className="prov-form__row">
            <TextField label="Address line 1" value={proposed.address_line_1} onChange={(e) => onProposed({ address_line_1: e.target.value })} />
            <TextField label="Address line 2" value={proposed.address_line_2} onChange={(e) => onProposed({ address_line_2: e.target.value })} />
          </div>
          <div className="prov-form__row">
            <TextField label="Address line 3" value={proposed.address_line_3} onChange={(e) => onProposed({ address_line_3: e.target.value })} />
            <TextField label="City" value={proposed.city} onChange={(e) => onProposed({ city: e.target.value })} />
          </div>
          <div className="prov-form__row">
            <TextField label="County" value={proposed.county} onChange={(e) => onProposed({ county: e.target.value })} />
            <TextField label="Postcode" value={proposed.postcode} onChange={(e) => onProposed({ postcode: e.target.value.toUpperCase() })} />
          </div>
          <fieldset className="prov-scopes">
            <legend>In scope</legend>
            <label className="prov-check">
              <input type="checkbox" checked={proposed.planning_in_scope} onChange={(e) => onProposed({ planning_in_scope: e.target.checked })} />
              Planning
            </label>
            <label className="prov-check">
              <input type="checkbox" checked={proposed.bc_in_scope} onChange={(e) => onProposed({ bc_in_scope: e.target.checked })} />
              Building control
            </label>
            <label className="prov-check">
              <input type="checkbox" checked={proposed.other_in_scope} onChange={(e) => onProposed({ other_in_scope: e.target.checked })} />
              Other drawings
            </label>
          </fieldset>
        </div>
      )}
    </article>
  );
}

/* ─── Client card ──────────────────────────────────────────────────────────── */

function ClientCard({
  plan,
  jobLinked,
  mode,
  proposed,
  linkedClientId,
  onMode,
  onPickCandidate,
  onProposed,
}: {
  plan: ProvisionPlan;
  jobLinked: boolean;
  mode: ClientMode;
  proposed: ProvisionProposedClient;
  linkedClientId: number | null;
  onMode: (m: ClientMode) => void;
  onPickCandidate: (clientId: number) => void;
  onProposed: (p: Partial<ProvisionProposedClient>) => void;
}) {
  const candidates = plan.suggestions.client.candidates;

  // Forced inherit when the job is linked — the card is read-only.
  if (jobLinked) {
    return (
      <article className="prov-card prov-card--disabled">
        <header className="prov-card__head">
          <h2>Client</h2>
          <Pill tone="neutral">Inherited</Pill>
        </header>
        <p className="prov-card__muted">Client inherited from the linked job.</p>
      </article>
    );
  }

  return (
    <article className="prov-card">
      <header className="prov-card__head">
        <h2>Client</h2>
        <ModeRadios
          name="client-mode"
          value={mode}
          options={[
            { value: "create", label: "Create new" },
            { value: "link", label: "Link existing", disabled: candidates.length === 0 },
          ]}
          onChange={(v) => onMode(v as ClientMode)}
        />
      </header>

      {mode === "link" ? (
        candidates.length === 0 ? (
          <p className="prov-card__muted">No matching clients to link.</p>
        ) : (
          <ul className="prov-candidates">
            {candidates.map((c: ProvisionClientCandidate) => (
              <li key={c.id}>
                <label className={`prov-candidate${linkedClientId === c.id ? " is-on" : ""}`}>
                  <input
                    type="radio"
                    name="client-candidate"
                    checked={linkedClientId === c.id}
                    onChange={() => onPickCandidate(c.id)}
                  />
                  <div className="prov-candidate__body">
                    <div className="prov-candidate__top">
                      <strong>{c.label}</strong>
                      <Pill tone="info-soft">Score {c.score}</Pill>
                    </div>
                    <span className="prov-candidate__sub">
                      {[c.email, c.postcode].filter(Boolean).join(" · ") || "—"}
                    </span>
                    {c.match_reason ? <span className="prov-candidate__reason">{c.match_reason}</span> : null}
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="prov-form">
          <label className="prov-check prov-check--inline">
            <input type="checkbox" checked={proposed.is_company} onChange={(e) => onProposed({ is_company: e.target.checked })} />
            Company client
          </label>
          {proposed.is_company ? (
            <TextField label="Company name" value={proposed.company_name} onChange={(e) => onProposed({ company_name: e.target.value })} />
          ) : null}
          <div className="prov-form__row">
            <TextField label="First name" value={proposed.contact_first_name} onChange={(e) => onProposed({ contact_first_name: e.target.value })} />
            <TextField label="Last name" value={proposed.contact_last_name} onChange={(e) => onProposed({ contact_last_name: e.target.value })} />
          </div>
          <div className="prov-form__row">
            <TextField label="Email" type="email" value={proposed.email} onChange={(e) => onProposed({ email: e.target.value })} />
            <TextField label="Phone" value={proposed.phone} onChange={(e) => onProposed({ phone: e.target.value })} />
          </div>
          <TextField label="Address line 1" value={proposed.address_line_1} onChange={(e) => onProposed({ address_line_1: e.target.value })} />
          <div className="prov-form__row">
            <TextField label="City" value={proposed.city} onChange={(e) => onProposed({ city: e.target.value })} />
            <TextField label="County" value={proposed.county} onChange={(e) => onProposed({ county: e.target.value })} />
          </div>
          <TextField label="Postcode" value={proposed.postcode} onChange={(e) => onProposed({ postcode: e.target.value.toUpperCase() })} />
        </div>
      )}
    </article>
  );
}

/* ─── Fees card ────────────────────────────────────────────────────────────── */

function FeesCard({
  fees,
  acceptedTotal,
  includedTotal,
  onChange,
}: {
  fees: FeeRow[];
  acceptedTotal: number;
  includedTotal: number;
  onChange: (rows: FeeRow[]) => void;
}) {
  function update(i: number, patch: Partial<FeeRow>) {
    onChange(fees.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }
  const differs = Math.abs(includedTotal - acceptedTotal) > 0.005;

  return (
    <article className="prov-card prov-card--wide">
      <header className="prov-card__head">
        <h2>Fees</h2>
        <span className="prov-card__muted">{fees.filter((f) => f.include).length} of {fees.length} included</span>
      </header>

      {fees.length === 0 ? (
        <p className="prov-card__muted">No accepted lines to turn into fees.</p>
      ) : (
        <div className="prov-fees">
          {fees.map((f, i) => (
            <div key={f.source_quote_line_id} className={`prov-fee${f.include ? "" : " prov-fee--off"}`}>
              <label className="prov-fee__include">
                <input type="checkbox" checked={f.include} onChange={(e) => update(i, { include: e.target.checked })} />
              </label>
              <TextField
                label="Description"
                className="prov-fee__desc"
                value={f.description}
                disabled={!f.include}
                onChange={(e) => update(i, { description: e.target.value })}
              />
              <TextField
                label="Fee type"
                className="prov-fee__type"
                value={f.fee_type}
                disabled={!f.include}
                onChange={(e) => update(i, { fee_type: e.target.value })}
              />
              <TextField
                label="Amount"
                type="number"
                className="prov-fee__amount"
                value={String(f.amount)}
                disabled={!f.include}
                onChange={(e) => update(i, { amount: Number(e.target.value) })}
              />
            </div>
          ))}
        </div>
      )}

      <footer className={`prov-fees__total${differs ? " prov-fees__total--warn" : ""}`}>
        <span>Included fees</span>
        <span className="numeric">{formatMoney(includedTotal)}</span>
        <span className="prov-fees__vs">vs accepted {formatMoney(acceptedTotal)}</span>
        {differs ? <span className="prov-fees__flag">Totals differ</span> : null}
      </footer>
    </article>
  );
}

/* ─── Drawing-set card ─────────────────────────────────────────────────────── */

const SEED_TYPES = [
  { value: "planning", label: "Planning" },
  { value: "building_control", label: "Building control" },
  { value: "other", label: "Other" },
];

function DrawingSetCard({
  linkedCandidate,
  linkedHasOpenSet,
  mode,
  label,
  seeds,
  onMode,
  onLabel,
  onSeeds,
}: {
  linkedCandidate: ProvisionJobCandidate | null;
  linkedHasOpenSet: boolean;
  mode: DrawingSetMode;
  label: string;
  seeds: SeedRow[];
  onMode: (m: DrawingSetMode) => void;
  onLabel: (label: string) => void;
  onSeeds: (seeds: SeedRow[]) => void;
}) {
  const current = linkedCandidate?.current_drawing_set ?? null;

  function setSeed(i: number, patch: Partial<SeedRow>) {
    onSeeds(seeds.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }
  function removeSeed(i: number) {
    onSeeds(seeds.filter((_, idx) => idx !== i));
  }
  function addSeed() {
    onSeeds([...seeds, { drawing_type: "planning", title: "" }]);
  }

  // When the linked job already has an open set, the user chooses Keep / Close &
  // new / Skip. Otherwise (new job, or linked-without-set) the only option is to
  // create the seeded set, with an include toggle (Create vs Skip).
  const showSeedEditor = mode === "create" || mode === "close_and_new";

  return (
    <article className="prov-card prov-card--wide">
      <header className="prov-card__head">
        <h2>Drawing set</h2>
        {linkedHasOpenSet ? (
          <ModeRadios
            name="ds-mode"
            value={mode}
            options={[
              { value: "keep", label: "Keep current" },
              { value: "close_and_new", label: "Close & new" },
              { value: "skip", label: "Skip" },
            ]}
            onChange={(v) => onMode(v as DrawingSetMode)}
          />
        ) : (
          <ModeRadios
            name="ds-mode"
            value={mode === "skip" ? "skip" : "create"}
            options={[
              { value: "create", label: "Create" },
              { value: "skip", label: "Skip" },
            ]}
            onChange={(v) => onMode(v as DrawingSetMode)}
          />
        )}
      </header>

      {linkedHasOpenSet && current ? (
        <div className="prov-ds-current">
          <strong>{current.label || `Set #${current.set_number}`}</strong>
          <span className="prov-card__muted">
            #{current.set_number} · {current.header_status} · {current.drawing_count} drawing
            {current.drawing_count === 1 ? "" : "s"}
            {current.is_complete ? " · complete" : ""}
          </span>
        </div>
      ) : null}

      {showSeedEditor ? (
        <div className="prov-ds-editor">
          <TextField label="Set label" value={label} onChange={(e) => onLabel(e.target.value)} />
          <div className="prov-ds-seeds">
            <div className="prov-ds-seeds__head">
              <span>Seed drawings</span>
              <Button variant="ghost" size="sm" onClick={addSeed}>+ Add drawing</Button>
            </div>
            {seeds.length === 0 ? (
              <p className="prov-card__muted">No seed drawings — add one, or commit an empty set.</p>
            ) : (
              seeds.map((s, i) => (
                <div key={i} className="prov-ds-seed">
                  <select
                    className="field__input field__select prov-ds-seed__type"
                    value={s.drawing_type}
                    onChange={(e) => setSeed(i, { drawing_type: e.target.value })}
                    aria-label="Drawing type"
                  >
                    {SEED_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <input
                    className="field__input prov-ds-seed__title"
                    value={s.title}
                    placeholder="Drawing title"
                    onChange={(e) => setSeed(i, { title: e.target.value })}
                    aria-label="Drawing title"
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeSeed(i)} title="Remove">✕</Button>
                </div>
              ))
            )}
          </div>
        </div>
      ) : mode === "keep" ? (
        <p className="prov-card__muted">Keeping the current open set — no change.</p>
      ) : (
        <p className="prov-card__muted">Skipping — no drawing set will be created.</p>
      )}
    </article>
  );
}

/* ─── Shared mode radios ───────────────────────────────────────────────────── */

function ModeRadios({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: string;
  options: { value: string; label: string; disabled?: boolean }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="prov-modes" role="radiogroup" aria-label={name}>
      {options.map((o) => (
        <label key={o.value} className={`prov-mode${value === o.value ? " is-on" : ""}${o.disabled ? " is-disabled" : ""}`}>
          <input
            type="radio"
            name={name}
            checked={value === o.value}
            disabled={o.disabled}
            onChange={() => onChange(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}
